// WebSocket + REST message contracts. Both client and server MUST validate
// incoming data with these schemas (server: always; client: always).
import { CHAT_MESSAGE_MAX, ChatMessageSchema, CigarSchema, FaceSchema } from "./lounge";
import { z } from "zod";
import { CardSchema } from "./cards";
import {
  DEFAULT_REVEAL_SECONDS,
  DEFAULT_TURN_SECONDS,
  LOBBY_NAME_MAX,
  LOBBY_NAME_MIN,
  LOBBY_PASSWORD_MAX,
  LOBBY_PASSWORD_MIN,
  MAX_HAND_LIMIT,
  MAX_REVEAL_SECONDS,
  MAX_SEATS,
  MAX_TURN_SECONDS,
  MIN_REVEAL_SECONDS,
  MIN_TURN_SECONDS,
  DISPLAY_NAME_MAX,
  DISPLAY_NAME_MIN,
  EMAIL_MAX,
  PASSWORD_MAX,
  PASSWORD_MIN,
  ROOM_CODE_LENGTH,
  USERNAME_MAX,
  USERNAME_MIN,
} from "./constants";
import { DEFAULT_ANTE, MAX_ANTE, MIN_ANTE } from "./economy";
import { ITEM_CATEGORIES } from "./items";
import { CastBodySchema, FxSchema, TableMagicSchema } from "./magic";

// ---------- Shared pieces ----------

/** Letters, numbers, space, underscore, hyphen, period. No leading/trailing space. */
export const UsernameSchema = z
  .string()
  .min(USERNAME_MIN)
  .max(USERNAME_MAX)
  .regex(/^[A-Za-z0-9_.-](?:[A-Za-z0-9_. -]*[A-Za-z0-9_.-])?$/, "Use letters, numbers, space, _ - .");

export const PasswordSchema = z.string().min(PASSWORD_MIN).max(PASSWORD_MAX);

/** Any characters, 1-30 after trimming; does not have to be unique. No control characters. */
export const DisplayNameSchema = z
  .string()
  .trim()
  .min(DISPLAY_NAME_MIN)
  .max(DISPLAY_NAME_MAX)
  .regex(/^[^\p{Cc}\p{Cf}]+$/u, "Display names cannot contain control characters.");

export const EmailSchema = z.string().trim().max(EMAIL_MAX).email("That does not look like an email address.");

/** Room code: ROOM_CODE_LENGTH letters/digits, case-insensitive (sent upper-case). */
export const RoomCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(new RegExp(`^[A-Z0-9]{${ROOM_CODE_LENGTH}}$`), "Room codes are 6 letters and numbers.");

const ItemCategoryIds = ITEM_CATEGORIES.map((c) => c.id) as [string, ...string[]];
export const ItemCategorySchema = z.enum(ItemCategoryIds);
export const ItemIdSchema = z.string().regex(/^\d{8}$/);
const ItemCategorySet: ReadonlySet<string> = new Set(ItemCategoryIds);
/**
 * Equipped item per category. Slots of removed categories (old accounts can
 * still have e.g. "table_theme" or "music") are dropped instead of failing.
 */
export const EquippedSchema = z.preprocess(
  (v) =>
    v !== null && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v as Record<string, unknown>).filter(([k]) => ItemCategorySet.has(k)))
      : v,
  z.partialRecord(ItemCategorySchema, ItemIdSchema),
);
export const WizardLookSchema = z.object({ hat: ItemIdSchema, robe: ItemIdSchema, head: ItemIdSchema, wand: ItemIdSchema.optional() });

export const LobbyNameSchema = z
  .string()
  .trim()
  .min(LOBBY_NAME_MIN)
  .max(LOBBY_NAME_MAX);

export const LobbyPasswordSchema = z.string().min(LOBBY_PASSWORD_MIN).max(LOBBY_PASSWORD_MAX);

export const SeatSchema = z.number().int().min(0).max(MAX_SEATS - 1);

/**
 * A host's rule for one spell: "banned" or "legal" (the default). Every spell
 * is legal now; "illegal" from older clients / saves is read as "legal".
 */
export const SpellRuleSchema = z
  .enum(["legal", "illegal", "banned"])
  .transform((r): "legal" | "banned" => (r === "banned" ? "banned" : "legal"));

export const LobbySettingsSchema = z.object({
  /** Ante every seated player pays each hand ("minimum entry cost"). */
  ante: z.number().int().min(MIN_ANTE).max(MAX_ANTE),
  turnSeconds: z.number().int().min(MIN_TURN_SECONDS).max(MAX_TURN_SECONDS),
  /** Unused (the board no longer flips face down); kept so older clients and saves still validate. */
  revealSeconds: z.number().int().min(MIN_REVEAL_SECONDS).max(MAX_REVEAL_SECONDS),
  spectatorsAllowed: z.boolean(),
  hideCardsFromSpectators: z.boolean(),
  /** Hands per session; null = unlimited (host ends the session). */
  handLimit: z.number().int().min(1).max(MAX_HAND_LIMIT).nullable(),
  /** Spells the host banned (spellId -> "banned"; the server keeps only "banned" entries). */
  spellRules: z.record(z.string(), SpellRuleSchema),
});
export type LobbySettings = z.infer<typeof LobbySettingsSchema>;

export const LobbySettingsPatchSchema = LobbySettingsSchema.partial();
export type LobbySettingsPatch = z.infer<typeof LobbySettingsPatchSchema>;

// ---------- REST: auth & account ----------

export const RegisterBodySchema = z.object({
  username: UsernameSchema,
  password: PasswordSchema,
  /** Optional; empty string = none. */
  email: z.union([EmailSchema, z.literal("")]).optional(),
});
export const LoginBodySchema = z.object({
  username: z.string().min(1).max(USERNAME_MAX),
  password: z.string().min(1).max(PASSWORD_MAX),
});

export const FreeOrbsSchema = z.object({ low: z.number().int(), middle: z.number().int(), high: z.number().int() });

export const WeeklyStatusSchema = z.object({
  /** Today's place in the week (UTC): 0 = Monday … 6 = Sunday. */
  today: z.number().int().min(0).max(6),
  /** Which days of this week (Monday first) were claimed. */
  claimed: z.array(z.boolean()).length(7),
  /** Epoch ms (server time) of the next 00:00 UTC, when the next day's reward opens. */
  nextDayAt: z.number().int(),
});
export type WeeklyStatus = z.infer<typeof WeeklyStatusSchema>;

export const MeSchema = z.object({
  userId: z.string(),
  username: z.string(),
  /** Shown to other players. Defaults to the username. */
  displayName: z.string(),
  email: z.string().nullable(),
  /** True when the account has a password (false for Discord/Google-only accounts). */
  hasPassword: z.boolean(),
  equipped: EquippedSchema,
  coins: z.number().int(),
  unlocks: z.array(z.string()),
  /** Free orb pulls waiting to be opened, per orb tier (from weekly rewards). */
  freeOrbs: FreeOrbsSchema,
  /** This week's login rewards (UTC weeks, Monday first). */
  weekly: WeeklyStatusSchema,
  isAdmin: z.boolean(),
  oauthProvider: z.enum(["discord", "google"]).nullable(),
  /** When the account last got BANKRUPT_REFILL_COINS for running out of coins (ms), or null. */
  refilledAt: z.number().nullable().optional(),
});
export type Me = z.infer<typeof MeSchema>;

export const WeeklyRewardSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("coins"), coins: z.number().int() }),
  z.object({ kind: z.literal("orb"), tier: z.enum(["low", "middle", "high"]) }),
]);
/** POST /api/me/rewards/claim — what today's reward gave, and the account after it. */
export const WeeklyClaimResponseSchema = z.object({ reward: WeeklyRewardSchema, me: MeSchema });
export type WeeklyClaimResponse = z.infer<typeof WeeklyClaimResponseSchema>;

export const OAuthProviderSchema = z.enum(["discord", "google"]);
export type OAuthProvider = z.infer<typeof OAuthProviderSchema>;

/** GET /api/auth/providers — which OAuth buttons to show. */
export const ProvidersResponseSchema = z.object({ discord: z.boolean(), google: z.boolean() });

// ---------- REST: Discord Activity ----------

/** Discord Activity instance ids, e.g. "i-1276580072400224306-gc-912952092627435520-912954213460484116". */
export const ActivityInstanceIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

/** GET /api/auth/discord/activity/config — the Discord application (client) id the Activity authorizes with. */
export const ActivityConfigResponseSchema = z.object({ clientId: z.string().regex(/^\d{5,32}$/) });

/** POST /api/auth/discord/activity — the code from the Embedded App SDK's commands.authorize(). */
export const ActivityLoginBodySchema = z.object({
  code: z.string().min(1).max(512),
  /** The Activity instance the player is in, to get a pass for its shared table. */
  instanceId: ActivityInstanceIdSchema.optional(),
});
export const ActivityLoginResponseSchema = z.object({
  me: MeSchema,
  /** Wizard Poker session, sent back as `Authorization: Bearer <token>` (no cookie inside Discord). */
  sessionToken: z.string().min(1).max(128),
  /** Discord access token for the SDK's commands.authenticate(). */
  accessToken: z.string().min(1).max(4096),
  /** Lets this account take a seat at the instance's shared table (POST /api/lobbies/activity). */
  instancePass: z.string().max(256).nullable(),
});
export type ActivityLoginResponse = z.infer<typeof ActivityLoginResponseSchema>;

/** POST /api/me/ws-ticket — one-time ticket for opening a lobby WebSocket without a cookie. */
export const WsTicketResponseSchema = z.object({ ticket: z.string().min(1).max(128) });

/** POST /api/lobbies/activity — join (or open) the table shared by everyone in one Activity instance. */
export const ActivityLobbyBodySchema = z.object({ instanceId: ActivityInstanceIdSchema, pass: z.string().min(1).max(256) });
export const ActivityLobbyResponseSchema = z.object({ id: z.string() });

// ---------- REST: account management (/api/me/*) and admin ----------

/** PATCH /api/me/profile */
export const UpdateProfileBodySchema = z.object({
  displayName: DisplayNameSchema.optional(),
  /** null or "" removes the email. */
  email: z.union([EmailSchema, z.literal(""), z.null()]).optional(),
});
/** POST /api/me/username */
export const ChangeUsernameBodySchema = z.object({ username: UsernameSchema });
/** POST /api/me/password */
export const ChangePasswordBodySchema = z.object({
  currentPassword: z.string().min(1).max(PASSWORD_MAX),
  newPassword: PasswordSchema,
});
/** POST /api/me/delete — password accounts confirm with the password; Discord/Google accounts type their username. */
export const DeleteAccountBodySchema = z.object({
  password: z.string().min(1).max(PASSWORD_MAX).optional(),
  confirmUsername: z.string().min(1).max(USERNAME_MAX).optional(),
});
/** POST /api/me/equip — itemId null takes the item off (the default is worn instead). */
export const EquipBodySchema = z.object({ category: ItemCategorySchema, itemId: ItemIdSchema.nullable() });
/** POST /api/admin/admins (admins only) */
export const SetAdminBodySchema = z.object({ username: z.string().min(1).max(USERNAME_MAX), isAdmin: z.boolean() });
export const SetAdminResponseSchema = z.object({ username: z.string(), isAdmin: z.boolean() });
/** Most coins an admin can give one account. */
export const ADMIN_COINS_MAX = 1_000_000_000_000;
/** POST /api/admin/coins (admins only): set the player's balance to exactly `coins`. */
export const AdminSetCoinsBodySchema = z.object({
  username: z.string().min(1).max(USERNAME_MAX),
  coins: z.number().int().min(0).max(ADMIN_COINS_MAX),
});
/** POST /api/admin/unlocks (admins only): give or take items. itemIds "all" means every item. */
export const AdminSetUnlocksBodySchema = z.object({
  username: z.string().min(1).max(USERNAME_MAX),
  itemIds: z.union([z.literal("all"), z.array(ItemIdSchema).min(1).max(500)]),
  owned: z.boolean(),
});
/** Most free orb pulls an admin can give in one command. */
export const ADMIN_ORBS_MAX = 50;
/**
 * POST /api/admin/orbs (admins only): give the player `count` free pulls of
 * an orb tier ("low" = Apprentice, "middle" = Magus, "high" = Archmage).
 * Responds with AdminOrbsResponseSchema; the player's lobbies get "accountChanged".
 */
export const AdminGiveOrbsBodySchema = z.object({
  username: z.string().min(1).max(USERNAME_MAX),
  tier: z.enum(["low", "middle", "high"]),
  count: z.number().int().min(1).max(ADMIN_ORBS_MAX),
});
export type AdminGiveOrbsBody = z.infer<typeof AdminGiveOrbsBodySchema>;
/** The player's free pulls after the gift. */
export const AdminOrbsResponseSchema = z.object({ username: z.string(), freeOrbs: FreeOrbsSchema });
export type AdminOrbsResponse = z.infer<typeof AdminOrbsResponseSchema>;

/**
 * POST /api/admin/lobbies/:id/close (admins only, no body): close any lobby like its host would
 * (the hand is settled / refunded, the lobby leaves the directory). Members get {t:"closed", reason:"admin"}.
 * 403 forbidden, 404 not_found.
 */
export const AdminCloseLobbyResponseSchema = z.object({ ok: z.literal(true) });
export type AdminCloseLobbyResponse = z.infer<typeof AdminCloseLobbyResponseSchema>;
/** What an admin command changed. */
export const AdminPlayerSchema = z.object({ username: z.string(), coins: z.number(), unlocks: z.array(z.string()) });
export type AdminPlayer = z.infer<typeof AdminPlayerSchema>;

// ---------- REST: feedback (Settings > Support) ----------

/** Longest bug report / suggestion, in characters (after trimming). */
export const FEEDBACK_MAX = 400;
export const FeedbackKindSchema = z.enum(["bug", "suggestion"]);
export type FeedbackKind = z.infer<typeof FeedbackKindSchema>;
/** POST /api/me/feedback */
export const FeedbackBodySchema = z.object({
  kind: FeedbackKindSchema,
  message: z
    .string()
    .trim()
    .min(1, "Write a message first.")
    .max(FEEDBACK_MAX, `Keep it to ${FEEDBACK_MAX} characters or fewer.`),
});
export type FeedbackBody = z.infer<typeof FeedbackBodySchema>;
/** One stored report, as admins see it. */
export const FeedbackEntrySchema = z.object({
  id: z.string(),
  username: z.string(),
  kind: FeedbackKindSchema,
  message: z.string(),
  createdAt: z.number(),
});
export type FeedbackEntry = z.infer<typeof FeedbackEntrySchema>;
/** GET /api/admin/feedback?before=<createdAt>&limit=<n> (admins only), newest first. */
export const FeedbackListResponseSchema = z.object({
  entries: z.array(FeedbackEntrySchema),
  /** Pass as `before` to get older entries; null when there are no more. */
  nextBefore: z.number().nullable(),
});
export type FeedbackListResponse = z.infer<typeof FeedbackListResponseSchema>;

// ---------- REST: messages from the team (admin broadcasts) ----------

/** Longest broadcast title and text, in characters (after trimming). */
export const NOTICE_TITLE_MAX = 80;
export const NOTICE_MESSAGE_MAX = 500;
/** Most usernames one targeted broadcast may name. */
export const NOTICE_TARGETS_MAX = 50;
/** POST /api/admin/broadcast (admins only): to "all" players online, or to these usernames. */
export const AdminBroadcastBodySchema = z.object({
  title: z
    .string()
    .trim()
    .max(NOTICE_TITLE_MAX, `Keep the title to ${NOTICE_TITLE_MAX} characters or fewer.`)
    .optional(),
  message: z
    .string()
    .trim()
    .min(1, "Write a message first.")
    .max(NOTICE_MESSAGE_MAX, `Keep the message to ${NOTICE_MESSAGE_MAX} characters or fewer.`),
  to: z.union([
    z.literal("all"),
    z
      .array(z.string().trim().min(1).max(USERNAME_MAX))
      .min(1, "Name at least one player.")
      .max(NOTICE_TARGETS_MAX, `Name at most ${NOTICE_TARGETS_MAX} players.`),
  ]),
});
export type AdminBroadcastBody = z.infer<typeof AdminBroadcastBodySchema>;
export const AdminBroadcastResponseSchema = z.object({
  /** Players online right now who were sent the message (best effort). */
  delivered: z.number().int(),
  /** Targeted only: names with no account. */
  notFound: z.array(z.string()),
  /** Targeted only: accounts not online right now (they see it if they come back within a day). */
  offline: z.array(z.string()),
});
export type AdminBroadcastResponse = z.infer<typeof AdminBroadcastResponseSchema>;
/** A message from the team, shown to players as a window. `id` grows with every message. */
export const NoticeSchema = z.object({
  id: z.number().int(),
  title: z.string().nullable(),
  message: z.string(),
  createdAt: z.number(),
});
export type Notice = z.infer<typeof NoticeSchema>;
/** GET /api/me/notices: messages for you not acknowledged yet, oldest first. */
export const NoticesResponseSchema = z.object({ notices: z.array(NoticeSchema) });
export type NoticesResponse = z.infer<typeof NoticesResponseSchema>;
/** POST /api/me/notices/ack: this message has been read (it is not shown again, on any device). */
export const NoticeAckBodySchema = z.object({ id: z.number().int().min(1) });

export const ApiErrorSchema = z.object({ error: z.string(), message: z.string() });
export type ApiError = z.infer<typeof ApiErrorSchema>;

// ---------- REST: lobbies ----------

export const LobbySummarySchema = z.object({
  id: z.string(),
  /** Room code to share (also shown for private lobbies; joining still needs the password). */
  code: z.string(),
  name: z.string(),
  isPrivate: z.boolean(),
  hostName: z.string(),
  players: z.number().int(),
  spectators: z.number().int(),
  maxPlayers: z.number().int(),
  status: z.enum(["waiting", "playing"]),
  ante: z.number().int(),
});
export type LobbySummary = z.infer<typeof LobbySummarySchema>;

export const LobbyListResponseSchema = z.object({ lobbies: z.array(LobbySummarySchema) });

export const CreateLobbyBodySchema = z.discriminatedUnion("isPrivate", [
  z.object({ name: LobbyNameSchema, isPrivate: z.literal(false) }),
  z.object({ name: LobbyNameSchema, isPrivate: z.literal(true), password: LobbyPasswordSchema }),
]);
export type CreateLobbyBody = z.infer<typeof CreateLobbyBodySchema>;
export const CreateLobbyResponseSchema = z.object({ id: z.string(), code: z.string() });

/** GET /api/lobbies/code/:code -> the lobby behind a room code. */
export const RoomCodeResponseSchema = z.object({ id: z.string(), isPrivate: z.boolean(), name: z.string() });

/** POST /api/lobbies/:id/join — admits the user; then the client opens the WebSocket. */
export const JoinLobbyBodySchema = z.object({ password: LobbyPasswordSchema.optional() });
export const JoinLobbyResponseSchema = z.object({ ok: z.literal(true) });

// ---------- WebSocket: views ----------

export const MemberViewSchema = z.object({
  userId: z.string(),
  username: z.string(),
  role: z.enum(["player", "spectator"]),
  seat: SeatSchema.nullable(),
  joinedAt: z.number().int(),
  connected: z.boolean(),
  look: WizardLookSchema,
  /**
   * Seated with fewer coins than the ante: "allin" = plays the next hand
   * all-in with what they have, "sitout" = sits out; null = not chosen yet
   * (sits out and is asked).
   */
  shortStack: z.enum(["allin", "sitout"]).nullable(),
  /** A cigar on their wizard (cosmetic). */
  cigar: CigarSchema,
  /** The face on their TV: their last reaction (or equipped face); null = the normal animated face. */
  face: FaceSchema.nullable(),
});
export type MemberView = z.infer<typeof MemberViewSchema>;

export const LobbyViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  isPrivate: z.boolean(),
  code: z.string(),
  hostId: z.string(),
  status: z.enum(["waiting", "playing"]),
  settings: LobbySettingsSchema,
  members: z.array(MemberViewSchema),
});
export type LobbyView = z.infer<typeof LobbyViewSchema>;

export const StreetSchema = z.enum(["preflop", "flop", "turn", "river", "showdown", "complete"]);

export const SeatViewSchema = z.object({
  seat: SeatSchema,
  userId: z.string(),
  username: z.string(),
  /**
   * The player's account coins right now (what they can still bet; coins already
   * bet this hand were taken from the account when bet). Kept in step with the
   * account while the hand runs (shop, orbs, rewards, other tabs).
   */
  stack: z.number().int(),
  streetBet: z.number().int(),
  totalBet: z.number().int(),
  inHand: z.boolean(),
  folded: z.boolean(),
  allIn: z.boolean(),
  /** Health hit 0 this hand (public, like the "knockout" fx): out until the next hand starts. Never in practice. */
  knockedOut: z.boolean(),
  /**
   * Always sent now (nullable only for older servers). Other players: base health and base juice (no
   * temporary health, no overcharge). Your own seat: health includes temporary health.
   */
  health: z.number().int().nullable(),
  mana: z.number().int().nullable(),
  /** Your own overcharge juice; null for other players. */
  overcharge: z.number().int().nullable(),
  handsWon: z.number().int(),
  /** Wizard look. */
  look: WizardLookSchema,
  /** Hole cards: real cards if the viewer may see them, otherwise null per card. */
  holeCards: z.array(CardSchema.nullable()),
  /**
   * The player's hole cards are flipped face up (they are peeking, or a Charm flipped them). Public; the
   * card faces stay hidden from others (holeCards stay null). Always sent by the server.
   */
  peeking: z.boolean().optional(),
  connected: z.boolean(),
});
export type SeatView = z.infer<typeof SeatViewSchema>;

export const HandResultViewSchema = z.object({
  reason: z.enum(["showdown", "fold", "house"]),
  payouts: z.array(z.object({ userId: z.string(), username: z.string(), amount: z.number().int() })),
  houseTook: z.number().int(),
  shown: z.array(
    z.object({ userId: z.string(), cards: z.array(CardSchema), handName: z.string() }),
  ),
});
export type HandResultView = z.infer<typeof HandResultViewSchema>;

export const TableViewSchema = z.object({
  handNumber: z.number().int(),
  street: StreetSchema,
  pot: z.number().int(),
  /** Community cards dealt so far, always face up (null only for a banished viewer). */
  community: z.array(CardSchema.nullable()),
  /** Unused: always null (there is no reveal window any more; the board stays face up). */
  revealUntil: z.number().int().nullable(),
  seats: z.array(SeatViewSchema),
  firstSeat: SeatSchema.nullable(),
  /** Seat whose turn it is; null when nobody is to act. */
  toActSeat: SeatSchema.nullable(),
  /**
   * Epoch ms (server clock) when toActSeat's turn times out; null when nobody is
   * to act. Not null does NOT mean betting is open: use `legal`.
   */
  turnDeadline: z.number().int().nullable(),
  currentBet: z.number().int(),
  /** Legal actions for the viewer, only while it is their turn. */
  legal: z
    .object({
      canFold: z.boolean(),
      canCheck: z.boolean(),
      callAmount: z.number().int(),
      /**
       * "allin" is always allowed on your turn. With canRaise false it is only
       * a call (the whole stack only if callAmount is the whole stack).
       */
      canRaise: z.boolean(),
      minRaiseTo: z.number().int(),
      maxRaiseTo: z.number().int(),
    })
    .nullable(),
  result: HandResultViewSchema.nullable(),
  handsPlayed: z.number().int(),
  handLimit: z.number().int().nullable(),
  magic: TableMagicSchema,
  /**
   * Pre-game practice table (lobby still waiting): spells work on its cards,
   * but health and juice are unlimited (never go down) and there is no betting.
   */
  practice: z.boolean().optional(),
});
export type TableView = z.infer<typeof TableViewSchema>;

export const SessionSummarySchema = z.object({
  handsPlayed: z.number().int(),
  /** Most hands won (ties all listed). No coin bonus. */
  winners: z.array(z.object({ userId: z.string(), username: z.string(), handsWon: z.number().int() })),
  players: z.array(z.object({ userId: z.string(), username: z.string(), handsWon: z.number().int(), net: z.number().int() })),
});
export type SessionSummary = z.infer<typeof SessionSummarySchema>;

// ---------- WebSocket: client -> server ----------

export const ClientMessageSchema = z.discriminatedUnion("t", [
  z.object({ t: z.literal("ping") }),
  /** Take the open seat the player clicked at the table (the only way to sit). */
  z.object({ t: z.literal("sit"), seat: SeatSchema }),
  /** Become a spectator (only between hands / when not in a hand; in-hand = fold). */
  z.object({ t: z.literal("stand") }),
  /** Host: start a session. */
  z.object({ t: z.literal("start") }),
  /** Host: end the session after the current hand. */
  z.object({ t: z.literal("endSession") }),
  z.object({
    t: z.literal("action"),
    action: z.discriminatedUnion("type", [
      z.object({ type: z.literal("fold") }),
      z.object({ type: z.literal("check") }),
      z.object({ type: z.literal("call") }),
      z.object({ type: z.literal("raise"), to: z.number().int().positive() }),
      z.object({ type: z.literal("allin") }),
    ]),
  }),
  /** Host: change settings (only while status = waiting, except spectator flags). */
  z.object({ t: z.literal("settings"), patch: LobbySettingsPatchSchema }),
  /** Host: kick a member. */
  z.object({ t: z.literal("kick"), userId: z.string() }),
  /** Host: set a new private-lobby password. */
  z.object({ t: z.literal("setPassword"), password: LobbyPasswordSchema }),
  z.object({ t: z.literal("leave") }),
  /** Seated with fewer coins than the ante: play all-in next hand, or sit out. */
  z.object({ t: z.literal("shortStack"), choice: z.enum(["allin", "sitout"]) }),
  /** Cast a spell (see magic.ts). */
  z.object({ t: z.literal("cast") }).extend(CastBodySchema.shape),
  /** Finished the minigame of an effect on you (escape, eyes, paint, spider, lock, fart). */
  z.object({ t: z.literal("effectDone"), effectId: z.string().min(1).max(40) }),
  /** Started / stopped looking at your hole cards (Ignite counts this). */
  z.object({ t: z.literal("peek"), on: z.boolean() }),
  /** Say something in the lobby chat (costs one chat token per character). */
  z.object({ t: z.literal("chat"), text: z.string().max(CHAT_MESSAGE_MAX * 2) }),
  /** Show a reaction (it stays as your face until the next one); null = back to your normal face. */
  z.object({ t: z.literal("react"), emote: FaceSchema.nullable() }),
  /** Take, light or put out a cigar. */
  z.object({ t: z.literal("cigar"), state: CigarSchema }),
  /** Host: close the lobby for everyone (the hand is settled first). */
  z.object({ t: z.literal("closeLobby") }),
  /** Your coins changed outside the lobby (slot machine, shop, rewards): the lobby re-reads them now (it is also told by the server). */
  z.object({ t: z.literal("syncCoins") }),
  /** Your look or owned faces changed (locker, shop, orb): re-read them from your account. */
  z.object({ t: z.literal("syncProfile") }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// ---------- WebSocket: server -> client ----------

export const ServerMessageSchema = z.discriminatedUnion("t", [
  z.object({ t: z.literal("pong") }),
  z.object({
    t: z.literal("welcome"),
    you: z.object({ userId: z.string(), username: z.string() }),
    lobby: LobbyViewSchema,
    table: TableViewSchema.nullable(),
    serverTime: z.number().int(),
  }),
  z.object({ t: z.literal("lobby"), lobby: LobbyViewSchema }),
  z.object({ t: z.literal("table"), table: TableViewSchema, serverTime: z.number().int() }),
  z.object({ t: z.literal("sessionEnd"), summary: SessionSummarySchema }),
  z.object({ t: z.literal("error"), code: z.string(), message: z.string() }),
  z.object({ t: z.literal("kicked") }),
  /** You are seated with fewer coins than the ante: choose all-in or sit out ("shortStack"). */
  z.object({ t: z.literal("shortStackPrompt"), ante: z.number().int(), balance: z.number().int() }),
  /** Spell events for animations and notices (the table view carries the state). */
  z.object({ t: z.literal("fx"), fx: z.array(FxSchema) }),
  /**
   * The lobby closed for good; the server closes the socket next.
   * - "idle": no activity in the lobby for LOBBY_IDLE_MS.
   * - "empty": the last member left. Their own sockets are already closed then,
   *   so normally nobody receives this one.
   * - "host": the host closed the lobby.
   * - "admin": an admin closed the lobby (POST /api/admin/lobbies/:id/close).
   */
  z.object({ t: z.literal("closed"), reason: z.enum(["idle", "empty", "host", "admin"]) }),
  /** A new chat message. */
  z.object({ t: z.literal("chat"), message: ChatMessageSchema }),
  /** Recent chat, sent after welcome. */
  z.object({ t: z.literal("chatHistory"), messages: z.array(ChatMessageSchema) }),
  /** Your chat budget after you sent something (or were refused). */
  z.object({ t: z.literal("chatStatus"), tokens: z.number().int(), mutedUntil: z.number().int() }),
  /** Someone's emote (shown on their face for REACTION_MS). */
  z.object({ t: z.literal("reaction"), userId: z.string(), emote: FaceSchema }),
  /** Your account coins after the table moved some (bet, ante, payout, refund): your whole balance. */
  z.object({ t: z.literal("coins"), coins: z.number().int() }),
  /**
   * Your account changed outside this table (shop, orb, locker, rewards, another
   * tab, an admin): fetch it again (GET /api/me). The table is updated already.
   */
  z.object({ t: z.literal("accountChanged") }),
  /** A message from the team (admin broadcast): show it as a window, then acknowledge it (POST /api/me/notices/ack). */
  z.object({ t: z.literal("notice"), notice: NoticeSchema }),
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

/**
 * Settings a new lobby starts with. The numbers come from economy.ts and
 * constants.ts: change them there, not here.
 */
export const DEFAULT_LOBBY_SETTINGS: LobbySettings = {
  ante: DEFAULT_ANTE,
  turnSeconds: DEFAULT_TURN_SECONDS,
  revealSeconds: DEFAULT_REVEAL_SECONDS,
  spectatorsAllowed: true,
  hideCardsFromSpectators: true,
  handLimit: null,
  spellRules: {},
};

/** GET /api/admin/stats?asOf=<ms> (admins only): account counts up to `asOf`. */
export const AdminStatsResponseSchema = z.object({
  asOf: z.number(),
  /** Accounts made on or before asOf (deleted ones left out). */
  accounts: z.number().int(),
  /** By how they sign in: username + password (website), Discord (website button or the Discord Activity), Google. */
  byType: z.object({ website: z.number().int(), discord: z.number().int(), google: z.number().int() }),
  /** Accounts that signed in during the day / week before asOf (from sessions still on record). */
  signedIn24h: z.number().int(),
  signedIn7d: z.number().int(),
  /** New accounts per day (YYYY-MM-DD, UTC) for the 14 days up to asOf, oldest first. */
  newPerDay: z.array(z.object({ day: z.string(), count: z.number().int() })),
});
export type AdminStatsResponse = z.infer<typeof AdminStatsResponseSchema>;
